/**
 * Console Adapter
 * Development-only adapter that logs to console
 */

import { BaseAnalyticsAdapter } from './adapter.interface'
import type {
  AdapterConfig,
  PageViewEvent,
  TrackEvent,
  IdentifyEvent,
  ErrorEvent,
  PerformanceEvent,
} from '../../domain/types'

/**
 * Console adapter for development
 * Logs all analytics events to the browser console
 */
export class ConsoleAdapter extends BaseAnalyticsAdapter {
  readonly name = 'console'

  private groupStyle = 'color: #6366f1; font-weight: bold;'
  private labelStyle = 'color: #94a3b8;'
  private valueStyle = 'color: #22c55e;'
  private collapsed = true

  async initialize(config: AdapterConfig): Promise<void> {
    await super.initialize(config)
    this.collapsed = (config.options as { collapsed?: boolean })?.collapsed ?? true
    console.log('%c[Analytics] Console adapter initialized', this.groupStyle)
  }

  trackPageView(event: PageViewEvent): void {
    if (!this.shouldTrack()) return

    const log = this.collapsed ? console.groupCollapsed : console.group
    log.call(
      console,
      '%c[Analytics] %cPage View: %c' + event.pagePath,
      this.groupStyle,
      this.labelStyle,
      this.valueStyle
    )
    console.log('Title:', event.title)
    console.log('Route:', event.routeName)
    console.log('Device:', event.deviceType)
    console.log('Previous Duration:', event.previousPageDuration, 'ms')
    console.log('Timestamp:', event.timestamp)
    console.groupEnd()
  }

  track(event: TrackEvent): void {
    if (!this.shouldTrack()) return

    const log = this.collapsed ? console.groupCollapsed : console.group
    log.call(
      console,
      '%c[Analytics] %cEvent: %c' + event.eventName,
      this.groupStyle,
      this.labelStyle,
      this.valueStyle
    )
    console.log('Category:', event.category)
    console.log('Action:', event.action)
    if (event.label) console.log('Label:', event.label)
    if (event.value !== undefined) console.log('Value:', event.value)
    if (event.properties) console.log('Properties:', event.properties)
    console.log('Timestamp:', event.timestamp)
    console.groupEnd()
  }

  identify(event: IdentifyEvent): void {
    if (!this.shouldTrack()) return

    const log = this.collapsed ? console.groupCollapsed : console.group
    log.call(
      console,
      '%c[Analytics] %cIdentify: %c' + event.userId,
      this.groupStyle,
      this.labelStyle,
      this.valueStyle
    )
    console.log('Traits:', event.traits)
    console.log('Timestamp:', event.timestamp)
    console.groupEnd()
  }

  trackError(event: ErrorEvent): void {
    // Always track errors regardless of consent
    const log = this.collapsed ? console.groupCollapsed : console.group
    log.call(
      console,
      '%c[Analytics] %cError: %c' + event.errorName,
      this.groupStyle,
      'color: #ef4444;',
      this.valueStyle
    )
    console.log('Message:', event.errorMessage)
    console.log('Severity:', event.severity)
    if (event.componentName) console.log('Component:', event.componentName)
    if (event.stackTrace) console.log('Stack:', event.stackTrace)
    if (event.fingerprint) console.log('Fingerprint:', event.fingerprint)
    console.log('Timestamp:', event.timestamp)
    console.groupEnd()
  }

  trackPerformance(event: PerformanceEvent): void {
    if (!this.shouldTrack()) return

    const ratingColor: Record<string, string> = {
      good: '#22c55e',
      'needs-improvement': '#f59e0b',
      poor: '#ef4444',
    }

    console.log(
      '%c[Analytics] %cPerformance: %c' +
        event.metricName +
        ' = %c' +
        (event.metricName === 'CLS' ? event.value.toFixed(3) : `${Math.round(event.value)}ms`) +
        '%c (' +
        event.rating +
        ')',
      this.groupStyle,
      this.labelStyle,
      'color: #3b82f6; font-weight: bold;',
      `color: ${ratingColor[event.rating]}; font-weight: bold;`,
      this.labelStyle
    )
  }

  reset(): void {
    console.log('%c[Analytics] User identity reset', this.groupStyle)
  }

  async flush(): Promise<void> {
    console.log('%c[Analytics] Flushing events', this.groupStyle)
    await super.flush()
  }
}
